import { createSlice, createAsyncThunk, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { Region, Province, Commune, Departement, GroupeClient, Typologie, User } from '../../types';
import { referenceApi } from '../../api/references';

interface ReferenceState {
  // Données en lecture seule (pas de CRUD)
  regions: Region[];
  provinces: Province[];
  communes: Commune[];
  
  // Données avec CRUD
  departements: Departement[];
  groupeClients: GroupeClient[];
  typologies: Typologie[];
  users: User[];
  
  loading: boolean;
  error: string | null;
}

const initialState: ReferenceState = {
  regions: [],
  provinces: [],
  communes: [],
  departements: [],
  groupeClients: [],
  typologies: [],
  users: [],
  loading: false,
  error: null,
};

// Actions en lecture seule pour les données géographiques
export const fetchRegions = createAsyncThunk(
  'references/fetchRegions',
  async () => {
    return await referenceApi.getRegions();
  }
);

export const fetchProvinces = createAsyncThunk(
  'references/fetchProvinces',
  async () => {
    return await referenceApi.getProvinces();
  }
);

export const fetchProvincesByRegion = createAsyncThunk(
  'references/fetchProvincesByRegion',
  async (regionId: number) => {
    return await referenceApi.getProvincesByRegion(regionId);
  }
);

export const fetchCommunes = createAsyncThunk(
  'references/fetchCommunes',
  async () => {
    return await referenceApi.getCommunes();
  }
);

export const fetchCommunesByProvince = createAsyncThunk(
  'references/fetchCommunesByProvince',
  async (provinceId: number) => {
    return await referenceApi.getCommunesByProvince(provinceId);
  }
);

// CRUD pour Departements
export const fetchDepartements = createAsyncThunk(
  'references/fetchDepartements',
  async () => {
    return await referenceApi.getDepartements();
  }
);

export const createDepartement = createAsyncThunk(
  'references/createDepartement',
  async (data: Partial<Departement>) => {
    return await referenceApi.createDepartement(data);
  }
);

export const updateDepartement = createAsyncThunk(
  'references/updateDepartement',
  async ({ id, data }: { id: number; data: Partial<Departement> }) => {
    return await referenceApi.updateDepartement(id, data);
  }
);

export const deleteDepartement = createAsyncThunk(
  'references/deleteDepartement',
  async (id: number) => {
    await referenceApi.deleteDepartement(id);
    return id;
  }
);

// CRUD pour GroupeClients
export const fetchGroupeClients = createAsyncThunk(
  'references/fetchGroupeClients',
  async () => {
    return await referenceApi.getGroupeClients();
  }
);

export const createGroupeClient = createAsyncThunk(
  'references/createGroupeClient',
  async (data: Partial<GroupeClient>) => {
    return await referenceApi.createGroupeClient(data);
  }
);

export const updateGroupeClient = createAsyncThunk(
  'references/updateGroupeClient',
  async ({ id, data }: { id: number; data: Partial<GroupeClient> }) => {
    return await referenceApi.updateGroupeClient(id, data);
  }
);

export const deleteGroupeClient = createAsyncThunk(
  'references/deleteGroupeClient',
  async (id: number) => {
    await referenceApi.deleteGroupeClient(id);
    return id;
  }
);

// CRUD pour Typologies
export const fetchTypologies = createAsyncThunk(
  'references/fetchTypologies',
  async () => {
    return await referenceApi.getTypologies();
  }
);

export const createTypologie = createAsyncThunk(
  'references/createTypologie',
  async (data: Partial<Typologie>) => {
    return await referenceApi.createTypologie(data);
  }
);

export const updateTypologie = createAsyncThunk(
  'references/updateTypologie',
  async ({ id, data }: { id: number; data: Partial<Typologie> }) => {
    return await referenceApi.updateTypologie(id, data);
  }
);

export const deleteTypologie = createAsyncThunk(
  'references/deleteTypologie',
  async (id: number) => {
    await referenceApi.deleteTypologie(id);
    return id;
  }
);

// CRUD pour Users
export const fetchUsers = createAsyncThunk(
  'references/fetchUsers',
  async () => {
    return await referenceApi.getUsers();
  }
);

export const createUser = createAsyncThunk(
  'references/createUser',
  async (data: Partial<User>) => {
    return await referenceApi.createUser(data);
  }
);

export const updateUser = createAsyncThunk(
  'references/updateUser',
  async ({ id, data }: { id: number; data: Partial<User> }) => {
    return await referenceApi.updateUser(id, data);
  }
);

export const deleteUser = createAsyncThunk(
  'references/deleteUser',
  async (id: number, { rejectWithValue }) => {
    try {
      await referenceApi.deleteUser(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const referenceSlice = createSlice({
  name: 'references',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<ReferenceState>) => {
    // Données géographiques (lecture seule)
    builder.addCase(fetchRegions.fulfilled, (state, action: PayloadAction<Region[]>) => {
      state.regions = action.payload;
    });
    
    builder.addCase(fetchProvinces.fulfilled, (state, action: PayloadAction<Province[]>) => {
      state.provinces = action.payload;
    });
    
    builder.addCase(fetchProvincesByRegion.fulfilled, (state, action: PayloadAction<Province[]>) => {
      state.provinces = action.payload;
    });
    
    builder.addCase(fetchCommunes.fulfilled, (state, action: PayloadAction<Commune[]>) => {
      state.communes = action.payload;
    });
    
    builder.addCase(fetchCommunesByProvince.fulfilled, (state, action: PayloadAction<Commune[]>) => {
      state.communes = action.payload;
    });

    // Departements CRUD
    builder.addCase(fetchDepartements.fulfilled, (state, action: PayloadAction<Departement[]>) => {
      state.departements = action.payload;
    });
    
    builder.addCase(createDepartement.fulfilled, (state, action: PayloadAction<Departement>) => {
      state.departements.push(action.payload);
    });
    
    builder.addCase(updateDepartement.fulfilled, (state, action: PayloadAction<Departement>) => {
      const index = state.departements.findIndex((d: Departement) => d.id === action.payload.id);
      if (index !== -1) state.departements[index] = action.payload;
    });
    
    builder.addCase(deleteDepartement.fulfilled, (state, action: PayloadAction<number>) => {
      state.departements = state.departements.filter((d: Departement) => d.id !== action.payload);
    });

    // GroupeClients CRUD
    builder.addCase(fetchGroupeClients.fulfilled, (state, action: PayloadAction<GroupeClient[]>) => {
      state.groupeClients = action.payload;
    });
    
    builder.addCase(createGroupeClient.fulfilled, (state, action: PayloadAction<GroupeClient>) => {
      state.groupeClients.push(action.payload);
    });
    
    builder.addCase(updateGroupeClient.fulfilled, (state, action: PayloadAction<GroupeClient>) => {
      const index = state.groupeClients.findIndex((gc: GroupeClient) => gc.id === action.payload.id);
      if (index !== -1) state.groupeClients[index] = action.payload;
    });
    
    builder.addCase(deleteGroupeClient.fulfilled, (state, action: PayloadAction<number>) => {
      state.groupeClients = state.groupeClients.filter((gc: GroupeClient) => gc.id !== action.payload);
    });

    // Typologies CRUD
    builder.addCase(fetchTypologies.fulfilled, (state, action: PayloadAction<Typologie[]>) => {
      state.typologies = action.payload;
    });
    
    builder.addCase(createTypologie.fulfilled, (state, action: PayloadAction<Typologie>) => {
      state.typologies.push(action.payload);
    });
    
    builder.addCase(updateTypologie.fulfilled, (state, action: PayloadAction<Typologie>) => {
      const index = state.typologies.findIndex((t: Typologie) => t.id === action.payload.id);
      if (index !== -1) state.typologies[index] = action.payload;
    });
    
    builder.addCase(deleteTypologie.fulfilled, (state, action: PayloadAction<number>) => {
      state.typologies = state.typologies.filter((t: Typologie) => t.id !== action.payload);
    });

    // Users CRUD
    builder.addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
      state.users = action.payload;
    });
    
    builder.addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
      state.users.push(action.payload);
    });
    
    builder.addCase(updateUser.fulfilled, (state, action: PayloadAction<User>) => {
      const index = state.users.findIndex((u: User) => u.id === action.payload.id);
      if (index !== -1) state.users[index] = action.payload;
    });
    
    builder.addCase(deleteUser.fulfilled, (state, action: PayloadAction<number>) => {
      state.users = state.users.filter((u: User) => u.id !== action.payload);
    });

    // États de chargement
    builder.addMatcher(
      (action) => action.type.endsWith('/pending'),
      (state) => {
        state.loading = true;
        state.error = null;
      }
    );
    
    builder.addMatcher(
      (action) => action.type.endsWith('/rejected'),
      (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'An error occurred';
      }
    );
    
    builder.addMatcher(
      (action) => action.type.endsWith('/fulfilled'),
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const { clearError } = referenceSlice.actions;
export default referenceSlice.reducer;